/**
 * EntityLinkedText.jsx - Renders text with clickable entity links
 * 
 * This component uses the unified entityIdProcessor for all ID handling.
 * It renders linkable entity IDs as clickable icons that open entity modals.
 * 
 * PROCESSING (via entityIdProcessor):
 * 1. Strips non-linkable IDs (prod_, comp_, simple kpi_, org_, user_)
 * 2. Suppresses the current entity ID (context-aware, no self-links)
 * 3. Finds all remaining linkable entities (including composite KPI keys)
 * 4. Renders text with clickable icons for each entity
 * 
 * COMPOSITE KPI HANDLING:
 * - Composite KPI keys (goal_xxx:kpi_xxx) are rendered as deep-links
 * - Clicking opens the Goal modal with KPI tab pre-selected
 * 
 * @example
 * <EntityLinkedText 
 *   text="Feature feat_123 relates to epic_456"
 *   excludeEntityId="feat_123"  // Won't show link for current item
 * />
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useEntityModal } from '../../contexts/EntityModalContext';
import { useModalZIndex } from '../../contexts/ModalZIndexContext';

// Import unified entity ID processor
import {
  processEntityIds,
  hasLinkableEntityIds,
  stripNonLinkableIds,
} from './utils/entityIdProcessor';

// =============================================================================
// LEGACY EXPORTS - For backward compatibility with existing imports
// =============================================================================

/**
 * @deprecated Use stripNonLinkableIds from entityIdProcessor instead
 * Maintained for backward compatibility with existing imports
 */
export const removeProductIds = stripNonLinkableIds;

/**
 * Check if text contains linkable entity IDs
 * Uses the unified processor's detection function
 * 
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains linkable entity IDs
 */
export const hasEntityIds = hasLinkableEntityIds;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * EntityLinkedText - Renders text with clickable entity links
 * 
 * Uses the unified entityIdProcessor for consistent ID handling across the app.
 * Renders an ExternalLink icon for each linkable entity that opens its modal.
 * 
 * Supports composite KPI keys (goal_xxx:kpi_xxx) by opening the Goal modal
 * with the KPI tab selected.
 * 
 * @param {string} text - The text to process and render
 * @param {string} className - Optional CSS classes for the wrapper span
 * @param {string} excludeEntityId - Entity ID to suppress (e.g., current item being viewed)
 */
const EntityLinkedText = ({ text, className = '', excludeEntityId = null }) => {
  const { openEntityModal } = useEntityModal();
  const currentZIndex = useModalZIndex();
  
  if (!text) return null;
  
  // Use unified processor for all ID handling
  // This handles: formatting cleanup, non-linkable stripping, suppression, and entity detection
  const { text: processedText, entities, hasLinks } = processEntityIds(text, {
    suppressEntityId: excludeEntityId,
  });
  
  // No linkable entities found - return clean text
  if (!hasLinks) {
    return <span className={className}>{processedText}</span>;
  }
  
  // Build result with clickable links
  const parts = [];
  let lastIndex = 0;
  
  entities.forEach((entity, idx) => {
    // Text before this entity
    if (entity.start > lastIndex) {
      parts.push(
        <span key={`text-${idx}`}>
          {processedText.slice(lastIndex, entity.start)}
        </span>
      );
    }
    
    // Handle composite KPI keys differently - they open Goal modal with KPI tab
    if (entity.isCompositeKpi) {
      parts.push(
        <button
          key={`entity-${idx}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Open Goal modal with KPI tab selected
            openEntityModal(entity.targetType, entity.goalId, { 
              zIndex: currentZIndex + 10,
              tab: entity.targetTab,  // 'kpis'
              kpiId: entity.kpiId,    // For highlighting specific KPI
            });
          }}
          className={cn(
            "inline-flex items-center cursor-pointer transition-opacity hover:opacity-70",
            entity.color
          )}
          title={`View KPI in Goal: ${entity.goalId}`}
          data-testid={`entity-link-kpi-composite-${entity.id}`}
        >
          <ExternalLink className="w-4 h-4 opacity-80" />
        </button>
      );
    } else {
      // Standard entity link
      parts.push(
        <button
          key={`entity-${idx}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Open the entity's modal at a higher z-index than current context
            openEntityModal(entity.type, entity.id, { zIndex: currentZIndex + 10 });
          }}
          className={cn(
            "inline-flex items-center cursor-pointer transition-opacity hover:opacity-70",
            entity.color
          )}
          title={`View ${entity.label}: ${entity.id}`}
          data-testid={`entity-link-${entity.type}-${entity.id}`}
        >
          <ExternalLink className="w-4 h-4 opacity-80" />
        </button>
      );
    }
    
    lastIndex = entity.end;
  });
  
  // Remaining text after the last entity
  if (lastIndex < processedText.length) {
    parts.push(
      <span key="text-end">
        {processedText.slice(lastIndex)}
      </span>
    );
  }
  
  return <span className={className}>{parts}</span>;
};

export default EntityLinkedText;
